import React from 'react';
import { useLingui } from '@lingui/react/macro';
import './SmartBlock.css';
import { CombatCalculator } from '@/core/battle/CombatCalculator';
import { BattleUnit, Skill, TerrainDef, TerrainType } from '@/core/types/battle.types';
import { TERRAINS } from '@/core/constants/battle.constants';
import { PathCalculator } from '@/core/battle/PathCalculator';
import { UnitPortrait } from '@/view/ui/battle/BottomPanel/UnitPortrait';
import { resolveAsset } from '@/core/utils/common.utils';

interface SmartBlockProps {
  skill?: Skill | null;
  terrain?: TerrainType | null;
  inspectedUnit?: BattleUnit | null;
  unit: BattleUnit;
}

export function SmartBlock({ unit, skill, terrain, inspectedUnit }: SmartBlockProps) {
  if (!skill && !terrain && !inspectedUnit) {
    return (
      <div className="smart-block hide-on-mobile-portrait">
        <div className="idle-state">
          <div className="idle-text"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="smart-block hide-on-mobile-portrait">
        {skill && <SkillDetails unit={unit} skill={skill} />}
        {!skill && inspectedUnit && <UnitInspect inspectedUnit={inspectedUnit} />}
        {!skill && !inspectedUnit && terrain && <TerrainBlock unit={unit} terrain={terrain} />}
    </div>
  );
  
}

const TERRAIN_ADDITIONAL: Partial<Record<TerrainType, TerrainDef>> = {
    [TerrainType.Hill]: { ...TERRAINS[TerrainType.Hill], sprite: resolveAsset('@assets/terrain/hill_edges.png'), textureSize: 256, spriteCols: 1, spriteRowY: 0 },
};

function getTerrainLayers(terrain: TerrainType): TerrainDef[] {
    const layers = [TERRAINS[terrain]];
    const additional = TERRAIN_ADDITIONAL[terrain];
    if (additional) layers.push(additional);
    return layers;
}

function TerrainLayer({ config }: { config: typeof TERRAINS[TerrainType] }) {
    const WINDOW = 80;
    const scale = WINDOW / config.textureSize;

    return (
        <div
            className="terrain-preview-layer"
            style={{
                backgroundImage: `url('${config.sprite}')`,
                backgroundSize: `${config.spriteCols * config.textureSize * scale}px ${WINDOW}px`,
                backgroundPosition: `0px -${config.spriteRowY * WINDOW}px`,
            }}
        />
    );
}

function TerrainBlock({ unit, terrain }: { unit: BattleUnit, terrain: string }) {
    const { t } = useLingui();
    const config = TERRAINS[terrain as TerrainType];
    if (!config) return null;

    const layers = getTerrainLayers(terrain as TerrainType);
    const powerMult = config.powerMult && config.powerMult !== 1 
        ? Math.round((config.powerMult - 1) * 100) 
        : 0;
    const moveCost = PathCalculator.getStepCost(unit.stats.speed, config.moveCost);

    return (
        <div className="skill-display">
            <div className="skills-column">
                <div className="skill-name">{t(config.name)}</div>
                <div className="stat-row">
                    <span className="label">{t`Move`}</span>
                    <span className="value">{moveCost}</span>
                </div>
                <div className="stat-row">
                    <span className="label">{t`Evasion`}</span>
                    <span className="value">{config.evasion ? `+${config.evasion}` : 0}</span>
                </div>
                <div className="stat-row">
                    <span className="label">{t`Attack`}</span>
                    <span className="value">{powerMult ? `+${powerMult}%` : 0}</span>
                </div>
            </div>
            <div className="skill-icon-module">
                <div className="skill-icon-content">
                    <div className="terrain-preview">
                        {layers.map((layerConfig, i) => (
                            <TerrainLayer key={i} config={layerConfig} />
                        ))}
                    </div>
                </div>
                <div className="skill-icon-frame" />
            </div>
        </div>
    );
}

function SkillDetails({ unit, skill }: { unit: BattleUnit, skill?: Skill }) {
  if (!skill || !unit) return <div className="skill-details empty"></div>;
  const { t, i18n } = useLingui();
  const effectiveCount = CombatCalculator.getEffectiveCount(unit.count);
  const minDmg = Math.round(unit?.stats.damageMin * effectiveCount * (skill.powerMult || 1));
  const maxDmg = Math.round(unit?.stats.damageMax * effectiveCount * (skill.powerMult || 1));

  const SKILL_TILE = 96;
  const SKILL_WINDOW = 80;
  const SCALE = SKILL_WINDOW / SKILL_TILE; // 1.25
  const col = skill.iconCol || 0;
  const row = skill.iconRow || 0;

  return (
    <div className="skill-display">
        
        <div className="skills-column">
            <div className="skill-name">{i18n._(skill.name)}</div>
            
            <div className="stat-row">
                <span className="label">{t`DAMAGE`}</span>
                <span className="value">{minDmg} - {maxDmg}</span>
            </div>
            <div className="stat-row">
                <span className="label">{t`RANGE`}</span>
                <span className="value">{skill.range}</span>
            </div>
            <div className="stat-row stamina-cost">
                <span className="label">{t`Cost`}</span>
                <span className="value">{skill.cost}</span>
            </div>
        </div>

        {/* Иконка в рамке */}
        <div className="skill-icon-module">
            <div className="skill-icon-content">
                {(skill.iconCol! >= 0) ? (
                    <div
                        className="skill-icon-sprite"
                        style={{
                            backgroundSize: `${3 * SKILL_TILE * SCALE}px ${5 * SKILL_TILE * SCALE}px`,
                            backgroundPosition: `-${col * SKILL_WINDOW}px -${row * SKILL_WINDOW}px`,
                        }}
                    />
                ) : (
                    <div className="skill-icon-placeholder">
                        {skill.name.charAt(0).toUpperCase()}
                    </div>
                )}
            </div>
            <div className="skill-icon-frame" />
        </div>
    </div>

  );
}

function UnitInspect({ inspectedUnit }: { inspectedUnit: BattleUnit }) {
  if (!inspectedUnit) return <></>;
  console.log(inspectedUnit.unitName, 'inspectedUnit.unitName');
  const { t, i18n } = useLingui();
  return (
    <div className="skill-display">
        
        <div className="stats-column stats-column-left">
            <div className="unit-name">{i18n._(inspectedUnit.unitName)}</div>
            <div className="stat-row">
                <span className="label">{t`DAMAGE`}</span>
                <span className="value">{inspectedUnit.stats.damageMin}-{inspectedUnit.stats.damageMax}</span>
            </div>
            <div className="stat-row">
                <span className="label">{t`ARMOR`}</span>
                <span className="value">{inspectedUnit.stats.armor}</span>
            </div>
            <div className="stat-row">
                <span className="label">{t`EVASION`}</span>
                <span className="value">{inspectedUnit.stats.evasion || 1}</span>
            </div>
        </div>
        <UnitPortrait unit={inspectedUnit} />
    </div>
  );
}

function IdleHints() {
  return (
    <div className="idle-hints">
      <div className="hint">Hover over skills for details</div>
      <div className="hint">Click on enemies to inspect</div>
    </div>
  );
}
